<template>
  <InputLabel
    :label="label"
    :tooltip="tooltip"
    :is-error="isError"
    :warning="warning"
    :class="props.class"
  >
    <div class="flex w-full items-center gap-3">
      <input ref="fileInput" class="hidden" type="file" :accept="accept" @change="onChange" />

      <CVButton
        type="button"
        variant="secondary"
        :disabled="disabled"
        :is-loading="isLoading"
        @click="openPicker"
      >
        {{ buttonText }}
      </CVButton>

      <div v-if="modelValue" class="min-w-0 flex-1 truncate text-sm text-obsidian-700">
        {{ modelValue.name }}
      </div>

      <CVButton
        v-if="clearable && modelValue"
        type="button"
        variant="ghost"
        :disabled="disabled || isLoading"
        @click="clear"
      >
        {{ clearText }}
      </CVButton>
    </div>

    <InputHint :hint="hint" :is-error="isError" />
  </InputLabel>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import InputLabel from './InputLabel.vue';
import InputHint from './InputHint.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  label?: string;
  hint?: string;
  isError?: boolean;
  tooltip?: string;
  warning?: string;
  modelValue?: File | null;
  accept?: string;
  disabled?: boolean;
  isLoading?: boolean;
  class?: string;
  buttonText?: string;
  clearText?: string;
  valueType?: 'auto' | 'file' | 'string';
  clearable?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: File | undefined): void;
}>();

const { t } = useI18n();

const fileInput = ref<HTMLInputElement | null>(null);

function openPicker() {
  fileInput.value?.click();
}

function onChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0] ?? undefined;
  emit('update:modelValue', file);
}

function clear() {
  emit('update:modelValue', undefined);
  if (fileInput.value) fileInput.value.value = '';
}

const buttonText = computed(() => props.buttonText ?? t('selectFile'));
const clearText = computed(() => props.clearText ?? t('reset'));
</script>

<i18n lang="json">
{
  "en": {
    "selectFile": "Select file",
    "reset": "Reset"
  },
  "de": {
    "selectFile": "Datei auswählen",
    "reset": "Zurücksetzen"
  },
  "es": {
    "selectFile": "Seleccionar archivo",
    "reset": "Restablecer"
  }
}
</i18n>
